/*
 *  TTTech nerve-management-system-frontend
 *  Copyright(c) 2026. TTTech Industrial Automation AG.
 *
 *  ALL RIGHTS RESERVED.
 *
 *  Usage of this software, including source code, netlists, documentation,
 *  is subject to restrictions and conditions of the applicable license
 *  agreement with TTTech Industrial Automation AG or its affiliates.
 *
 *  All trademarks used are the property of their respective owners.
 *
 *  TTTech Industrial Automation AG and its affiliates do not assume any liability
 *  arising out of the application or use of any product described or shown
 *  herein. TTTech Industrial Automation AG and its affiliates reserve the right to
 *  make changes, at any time, in order to improve reliability, function or
 *  design.
 *
 *  Contact Information:
 *  support@tttech-industrial.com
 *
 *  TTTech Industrial Automation AG, Schoenbrunnerstrasse 7, 1040 Vienna, Austria
 *
 */

import { v1 as uuidv1 } from 'uuid';
import { defineStore } from 'pinia';
import ConnectionTunnel from '@/model/node/connectionTunnel.model';
import ConnectionScreen from '@/model/node/connectionScreen.model';
import RemoteConnectionModel from '@/model/node/remoteConnection.model';
import { RemoteConnectionApiService, WorkloadsApiService } from '@/services/api';
import useUtilsStore from '@/store/modules/pinia/utils/_api_request_handler';
import { TYPE_OF_WORKLOADS } from '@/constants';

// Error message related to RC status
const errors = {
  declined: 'remoteConnection.guacamoleClientMsnConnectionRejected',
  disconnected: 'remoteConnection.guacamoleClientMsnConnectionDisconnected',
  session_expired: 'remoteConnection.guacamoleClientMsnConnectionSessionExpired',
  link_expired: 'remoteConnection.ncmLinkExpired',
};

const useRemoteConnectionStore = defineStore('remoteConnection', {
  state: () => ({
    remoteConnectionList: [],
    rcTunnelDialog: false,
    rcScreenDialog: false,
    rcTunnel: {},
    rcScreen: {},
    edit: {},
    isOpenCloseInitConnectionDialog: false,
    remoteConnectionWorkloadDialogState: false,
    isOpenConnectingDialog: false,
    reqId: '',
    connected: false,
    activeConnRequestUid: null,
    connectionType: {},
    rc: {},
    wlRc: {},
    workloadType: '',
    selectedNodeId: '',
    selectedWorkloadId: '',
    listOfServices: [],
    expiresAt: null,
  }),

  actions: {
    setRcTunnelDialog(value) {
      this.rcTunnelDialog = value;
    },

    setRcScreenDialog(value) {
      this.rcScreenDialog = value;
    },

    clearRcTunnelHostnameAndPort() {
      this.rcTunnel.hostname = '';
      this.rcTunnel.port = '';
    },

    setPortForRcScreen(port) {
      this.rcScreen.port = port;
    },

    setOpenInitConnectionDialog(value) {
      this.isOpenCloseInitConnectionDialog = value;
    },

    setOpenConnectingDialog(value) {
      this.isOpenConnectingDialog = value;
    },

    setConnected(value) {
      this.connected = value;
    },

    clearRcScreenPrivateKey() {
      this.rcScreen.privateKey = '';
    },

    loadRemoteConnections(data) {
      if (data?.remoteConnections?.length) {
        this.remoteConnectionList = data.remoteConnections.map((rc) => new RemoteConnectionModel({ ...rc }));
        return;
      }
      this.remoteConnectionList = [];
    },

    addUpdateRemoteConnection({ connection }) {
      // add new rc during creation of wl version
      // rc already exist -> edit the existing rc
      if (connection.index !== undefined) {
        this.remoteConnectionList.splice(connection.index, 1, new RemoteConnectionModel(connection));
        return;
      }
      // create a new rc
      this.remoteConnectionList.push(new RemoteConnectionModel(connection));
    },

    updateNode({ nodeData }) {
      if (nodeData.delete) {
        this.remoteConnectionList.splice(nodeData.index, 1);
        return;
      }
      this.addUpdateRemoteConnection({ connection: nodeData.connection });
    },

    deleteRemoteConnection(params) {
      this.remoteConnectionList.splice(params.index, 1);
    },

    getTunnelRc(params) {
      const { index } = params;
      const rcTunnel = this.remoteConnectionList[index];
      this.rcTunnel = new ConnectionTunnel(rcTunnel);
      this.rcTunnel.oldName = this.rcTunnel.name;
      this.rcTunnel.index = index;
    },

    getScreenRc(params) {
      const { index } = params;
      const rcScreen = this.remoteConnectionList[index];
      this.rcScreen = new ConnectionScreen(rcScreen);
      this.rcScreen.oldName = this.rcScreen.name;
      this.rcScreen.index = index;
    },

    /**
     * Send an API request to BE in order to establish a connection
     */
    async remoteConnect(remoteConnection) {
      // Generate unique id of connection request(NERVESW-9868)
      remoteConnection.uniqueConnectionRequestNo = uuidv1();

      this.rc = remoteConnection;

      // Remember the connectionRequestUID in the store
      // It is used to match mqtt message which arrives from BE
      this.reqId = remoteConnection.uniqueConnectionRequestNo;

      const payload = await RemoteConnectionApiService.remoteConnect(remoteConnection);

      if (payload && payload.error) {
        // Close connecting dialog(dialog with a spinner and message Waiting...)
        this.isOpenConnectingDialog = false;
        const utilsStore = useUtilsStore();

        // temp solution to display specific error message instead of generic one
        if (payload.errVersionMismatch) {
          utilsStore.showCustomToast({
            text: 'remoteConnection.managementToastFailedVersionMismatch',
            color: 'red',
            showClose: true,
          });
          return;
        }

        if (payload.error.code) {
          utilsStore.showCustomToast({
            text: payload.error.code,
            color: 'red',
            showClose: true,
          });
          return;
        }
        // A generic error message
        utilsStore.showCustomToast({
          text: 'remoteConnection.managementToastFailed',
          color: 'red',
          showClose: true,
        });
      }

      /*
      CONNECTED is not a part of the API response when establishing a connection for the first time
      */
      if (typeof payload.connected === 'boolean') {
        this.connected = payload.connected;
      }
      this.activeConnRequestUid = payload.requestUid;
      this.connectionExpired = payload?.expiresAt;

      if (!payload.connected) {
        return;
      }
      if (payload.type) {
        this.connectionType = payload;
      }
    },

    async cancelRemoteConnApproval(activeConnRequestUid) {
      this.activeConnRequestUid = null;
      await RemoteConnectionApiService.cancelRemoteConnApproval(activeConnRequestUid);
    },

    async handleMqttRemoteConnectionResponse(payload) {
      if (this.reqId !== payload.requestId) {
        return;
      }
      this.expiresAt = payload?.expiresAt ? new Date(payload.expiresAt).toLocaleString('en-GB') : null;

      // If the mqtt message is received before the API request is completed
      // Store connection request UID
      this.activeConnRequestUid = payload.requestId;

      if (payload.status === 'connected') {
        if (payload.type === 'TUNNEL') {
          this.connected = true;
        }

        if (payload.type) {
          this.connectionType = payload;
        }
        return;
      }

      // Close open dialog
      this.isOpenConnectingDialog = false;

      const showToast = async (text) => {
        const utilsStore = useUtilsStore();
        utilsStore.showCustomToast({
          text,
          color: 'red',
          showClose: true,
        });
      };

      if (payload.status === 'failed') {
        const errorMessage = payload?.error?.code || payload.error;
        await showToast(errorMessage);
      }

      if (payload.status === 'session_expired') {
        // Toast has to be delayed to be visible
        setTimeout(async () => {
          await showToast(errors[payload.status]);
        }, 3000);
      } else {
        await showToast(errors[payload.status]);
      }
    },

    async guacamoleConnectionTab(data) {
      await RemoteConnectionApiService.guacamoleConnectionTab(data);
    },

    setExpiresAt(expiresAt) {
      this.expiresAt = expiresAt ? new Date(expiresAt).toLocaleString('en-GB') : null;
    },

    setSelectedNodeOrWlId(payload) {
      const { type, selectedId } = payload;
      if (type === 'workload') {
        this.selectedWorkloadId = selectedId;
        return;
      }
      this.selectedNodeId = selectedId;
    },

    async importRemoteConnections(params) {
      const response = await RemoteConnectionApiService.validateImportedRemoteConnections(params);
      const { remoteConnections } = response;
      this.remoteConnectionList = remoteConnections.map((r) => new RemoteConnectionModel({ ...r }));
      return response;
    },
    async getWorkloadVersionRc(data) {
      const workload = await WorkloadsApiService.getWorkloadVersionRc(data);
      const res = workload.versions && workload.versions[0] && workload.versions[0].remoteConnections;
      this.wlRc = res;
      // If workload is compose, set list of services.
      // This is needed to match if service defined in remote connection is defined in compose workload
      if (workload.type === TYPE_OF_WORKLOADS.COMPOSE) {
        workload?.versions[0]?.files.forEach((file) => {
          if (file.sourceInfo.type === 'compose') {
            const fileContent = JSON.parse(file.sourceInfo.content);
            if (fileContent && fileContent.services) {
              this.listOfServices = Object.getOwnPropertyNames(fileContent.services);
            }
          }
        });
      }
    },
    setWorkloadType(workloadType) {
      this.workloadType = workloadType;
    },
    setRemoteConnectionsWorkloadDialogState(value) {
      this.remoteConnectionWorkloadDialogState = value;
    },
    async deleteRcFromNode({ remoteConnectionId, serialNumber }) {
      await RemoteConnectionApiService.deleteRcFromNode({ remoteConnectionId, serialNumber });
    },
    async addRemoteConnection({ target, remoteConnection }) {
      await RemoteConnectionApiService.addRemoteConnection({ target, remoteConnection });
    },
    async editRemoteConnection({ target, remoteConnection, remoteConnectionId }) {
      await RemoteConnectionApiService.editRemoteConnection({
        target,
        remoteConnection,
        remoteConnectionId,
      });
    },
    async importRemoteConnectionsToNode({ serialNumber, formData }) {
      await RemoteConnectionApiService.importRemoteConnections({
        serialNumber,
        formData,
      });
    },
    mqttRemoteConnectionsUpdated({ nodeId, message }) {
      if (nodeId !== this.selectedNodeId) {
        return;
      }
      const { changes } = message;
      if (!Object.prototype.hasOwnProperty.call(changes, 'remoteConnections')) {
        return;
      }
      this.remoteConnectionList = changes.remoteConnections?.map((rc) => new RemoteConnectionModel({ ...rc })) || [];
    },
  },
});

export default useRemoteConnectionStore;
